// ONE way to name a configuration — M82d put the rule here, M97a inverted it.
//
// Five surfaces name the same thing, and before M82d they disagreed:
//
//   buildInventoryOptions   the Add Configuration picker (Live Fire, Dry Fire)
//   buildSessionOptions     the ammo and magazine "which configuration?" pickers
//   attributionLabel        the trip-line chips and the split editor
//   derivedPackRows         the packing list's group titles
//   the session Firearms row
//
// ── THE RULE IS BUILD-FIRST (M97a, D1) ──────────────────────────────────────
//
// M82d's D14 "collapse" printed the GUN whenever it had ≤1 active build, on
// the theory that a lone build called "Factory" is noise. In practice every
// gun the owner has carries exactly one active build, and he NAMED them —
// `11.5" SBR`, `G45`, `LWRC Factory 16"` — so the collapse printed inventory
// names on every range-day surface and the names he gave never appeared
// anywhere. The complaint was "the list looks like inventory". It did,
// because the rule printed inventory.
//
// Now the build's name is the name. The only build that prints as its gun is
// one called exactly `FACTORY_BUILD_NAME` (or no build at all: a legacy row).
// Keyed on the NAME, never `is_factory_stock` (R17) — four of the owner's
// factory-stock builds carry his own names.
//
// ── ONE FUNCTION, THREE STRINGS (DD3) ───────────────────────────────────────
//
//   label      one line, self-sufficient. The build's name; on a COLLISION —
//              the same name on another gun anywhere in the pool — it becomes
//              "name · gun" (R6). For input text after a pick, trip-line
//              chips, the split editor, the packing-list group title.
//   primary    the bare build name (the gun when Factory/null). Line 1 of a
//              two-line surface (dropdown option, session row), which never
//              needs the suffix because line 2 disambiguates.
//   secondary  "gun · caliber" — or the caliber alone when `primary` already
//              IS the gun, so no surface ever says the gun twice. Line 2 of
//              the dropdown and the session row; the picker's helper after
//              a pick ("On Glock 19 · 9mm", D12).
//
// ── THE POOL IS ALWAYS EVERY ACTIVE BUILD YOU OWN (R12, E2) ─────────────────
//
// The collision set must be the same on every surface, or two surfaces name
// one row differently — exactly the three-names bug M82d fixed. So the pool is
// a REQUIRED argument at every entry point, and it is the page's full
// `allBuilds` query, never a per-surface subset (a session picker whose
// session holds one gun still shows the suffix when the other gun exists in
// inventory). A pool that failed to load (`[]`) degrades to no suffix, which
// is never wrong, only less specific; the page's fetch-honesty line says why.
//
// RETROACTIVITY, on purpose: the day a second build takes an existing name,
// every existing label of that name gains its gun suffix. That is the rule
// working, not a regression — see DESIGN.md "Naming a configuration".
//
// WHERE THIS LIVES. `rangeReconcile.ts` and `magCloseOut.ts` are libs and must
// not import from a page, so this sits in `src/lib/`. It imports nothing but
// the factory-name leaf so it stays a pure lib.

import { FACTORY_BUILD_NAME } from './factoryBuildName';

/** A firearm with no make/model still needs a non-empty name. */
export const UNKNOWN_FIREARM_LABEL = 'Unknown';

export function firearmLabel(f: { make?: string | null; model?: string | null } | null | undefined): string {
  if (!f) return UNKNOWN_FIREARM_LABEL;
  return `${f.make ?? ''} ${f.model ?? ''}`.trim() || UNKNOWN_FIREARM_LABEL;
}

/** The three strings every surface renders from. */
export interface ConfigurationName {
  /** One line, self-sufficient: the build, suffixed with its gun on a collision. */
  label: string;
  /** Line 1 of a two-line surface: the bare build name (the gun when Factory/null). */
  primary: string;
  /** Line 2: "gun · caliber", or the caliber alone when `primary` is the gun. '' when nothing to say. */
  secondary: string;
}

/** What the collision check needs from each build in the pool. */
export type ConfigurationPool = ReadonlyArray<{
  name: string;
  firearm_id: string;
  /** Absent = active (a pre-filtered query passes rows without the flag). */
  is_active?: boolean | null;
}>;

/** Does any ACTIVE build on a DIFFERENT gun carry this name? */
function collidesInPool(build: { name: string; firearm_id: string }, pool: ConfigurationPool): boolean {
  const name = build.name.trim();
  for (const b of pool) {
    if (b.is_active === false) continue;
    if (b.firearm_id === build.firearm_id) continue;
    if (b.name.trim() === name) return true;
  }
  return false;
}

/**
 * The name of a configuration, as every surface should render it.
 *
 * @param gun    the anchor firearm: its display label and caliber
 * @param build  the build on the row, or null (a legacy row, or a gun still awaiting one)
 * @param pool   ALL active builds in inventory — the collision set (R12). Required.
 */
export function configurationLabel(
  gun: { label: string; caliber?: string | null },
  build: { name: string; firearm_id: string } | null,
  pool: ConfigurationPool,
): ConfigurationName {
  const gunName = gun.label.trim() || UNKNOWN_FIREARM_LABEL;
  const caliber = gun.caliber?.trim() || '';
  const buildName = build?.name.trim() ?? '';

  // No name worth printing: a legacy row, a blank name, or the factory default.
  // The gun is the name, and line 2 has only the caliber left to say.
  if (!build || !buildName || buildName === FACTORY_BUILD_NAME) {
    return { label: gunName, primary: gunName, secondary: caliber };
  }

  const collision = collidesInPool({ name: buildName, firearm_id: build.firearm_id }, pool);
  return {
    label: collision ? `${buildName} · ${gunName}` : buildName,
    primary: buildName,
    secondary: caliber ? `${gunName} · ${caliber}` : gunName,
  };
}
